import { z } from 'zod'

import { extractOrderItemChatRaw, parseChatHistory, orderItemHasChatData } from './chat-history'
import { toPagination } from './pagination'

const orderBillingRuleSchema = z.enum(['SENDER', 'RECEIVER'])

/** Range toggle above the summary cards. */
export const dashboardPeriodSchema = z.enum(['30d', '90d'])

/**
 * One status column. `amount` is the summed `total_order_price` for orders in that
 * status (BE `*_revenue` fields on the group list response).
 */
export const orderBucketSchema = z.object({
  count: z.number().int().nonnegative(),
  amount: z.number().nonnegative().nullable(),
})

export const groupSchema = z.object({
  id: z.string(),
  name: z.string(),
  whatsappGroupId: z.string(),
  isVerified: z.boolean(),
  verificationDeniedReason: z.string().nullable(),
  merchantName: z.string().nullable(),
  merchantAddress: z.string().nullable(),
  merchantRouteId: z.number().nullable(),
  logoUrl: z.string().nullable(),
  buckets: z.object({
    new: orderBucketSchema,
    billed: orderBucketSchema,
    processing: orderBucketSchema,
    shipped: orderBucketSchema,
  }),
  completedCount: z.number().int().nonnegative(),
})

export const paginationSchema = z.object({
  page: z.number().int(),
  perPage: z.number().int(),
  totalPages: z.number().int(),
  totalCount: z.number().int(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
})

export const groupListSchema = z.object({
  items: z.array(groupSchema),
  pagination: paginationSchema,
})

/**
 * Aggregates shown in the header cards. Money and period-over-period deltas are
 * nullable: no backend aggregation exists for them yet (PRD review Q7), so the
 * UI renders a dash rather than inventing a number.
 */
export const dashboardSummarySchema = z.object({
  revenue: z.object({ amount: z.number().nullable(), deltaPercent: z.number().nullable() }),
  completedOrders: z.object({ count: z.number().int(), deltaPercent: z.number().nullable() }),
  unpaid: z.object({ amount: z.number().nullable(), orderCount: z.number().int() }),
  activeGroups: z.object({ count: z.number().int(), withoutOrders: z.number().int() }),
})

/** Wire format of `GET /v1/algo-pos-be/liotitip/group`, mapped to the model above. */
const groupPayloadSchema = z
  .object({
    external_id: z.string(),
    name: z.string(),
    group_id: z.string().nullish(),
    is_verified: z.boolean().nullish(),
    verification_denied_reason: z.string().nullish(),
    unpaid_counter: z.number().int().nonnegative().nullish(),
    unpaid_revenue: z.coerce.number().nonnegative().nullish(),
    billed_counter: z.number().int().nonnegative().nullish(),
    billed_revenue: z.coerce.number().nonnegative().nullish(),
    in_process_counter: z.number().int().nonnegative().nullish(),
    in_process_revenue: z.coerce.number().nonnegative().nullish(),
    shipped_counter: z.number().int().nonnegative().nullish(),
    shipped_revenue: z.coerce.number().nonnegative().nullish(),
    completed_counter: z.number().int().nonnegative().nullish(),
    group_merchant: z
      .object({
        name: z.string().nullish(),
        address: z.string().nullish(),
        logo_url: z.string().nullish(),
        logo: z.string().nullish(),
        logo_path: z.string().nullish(),
        route_id: z.coerce.number().nullish(),
      })
      .nullish(),
  })
  .transform((payload) => ({
    id: payload.external_id,
    name: payload.name,
    whatsappGroupId: payload.group_id ?? '',
    isVerified: payload.is_verified ?? false,
    verificationDeniedReason: payload.verification_denied_reason || null,
    merchantName: payload.group_merchant?.name || null,
    merchantAddress: payload.group_merchant?.address || null,
    merchantRouteId: payload.group_merchant?.route_id ?? null,
    logoUrl:
      payload.group_merchant?.logo_url ||
      payload.group_merchant?.logo ||
      payload.group_merchant?.logo_path ||
      null,
    buckets: {
      new: {
        count: payload.unpaid_counter ?? 0,
        amount: payload.unpaid_revenue ?? null,
      },
      billed: {
        count: payload.billed_counter ?? 0,
        amount: payload.billed_revenue ?? null,
      },
      processing: {
        count: payload.in_process_counter ?? 0,
        amount: payload.in_process_revenue ?? null,
      },
      shipped: {
        count: payload.shipped_counter ?? 0,
        amount: payload.shipped_revenue ?? null,
      },
    },
    completedCount: payload.completed_counter ?? 0,
  }))

const paginationPayloadSchema = z
  .object({
    page: z.number().int().nullish(),
    per_page: z.number().int().nullish(),
    total_pages: z.number().int().nullish(),
    total_count: z.number().int().nullish(),
    has_next: z.boolean().nullish(),
    has_prev: z.boolean().nullish(),
  })
  .nullish()

export const groupListPayloadSchema = z
  .object({
    groups: z.array(groupPayloadSchema).nullish(),
    pagination: paginationPayloadSchema,
  })
  .transform((payload) => ({
    items: payload.groups ?? [],
    pagination: toPagination(payload.pagination, payload.groups?.length ?? 0),
  }))

/** True when at least one connected group passed verification (onboarding gate). */
export function hasAnyVerifiedGroupItem(items: readonly Group[] | undefined) {
  return items?.some((group) => group.isVerified) ?? false
}

/** Groups shown in the dashboard "Grup Kamu" table (verified bindings only). */
export function listVerifiedGroupItems(items: readonly Group[]) {
  return items.filter((group) => group.isVerified)
}

/** Unverified groups — hidden from the main table; surfaced via the failure drawer. */
export function listUnverifiedGroupItems(items: readonly Group[]) {
  return items.filter((group) => !group.isVerified)
}

export function hasAnyVerifiedGroup(data: GroupList | undefined) {
  return hasAnyVerifiedGroupItem(data?.items)
}

/**
 * Order lifecycle as the backend reports it. `UNKNOWN` is a catch-all so a status
 * added later degrades to a neutral chip instead of failing the whole page.
 */
export const groupOrderStatusSchema = z
  .enum(['UNPAID', 'BILLED', 'IN_PROCESS', 'SHIPPED', 'COMPLETED', 'CANCELLED', 'UNKNOWN'])
  .catch('UNKNOWN')

/** Empty on orders whose invoice has not been raised yet. */
export const invoiceStatusSchema = z.enum(['PENDING', 'PAID', 'EXPIRED']).nullable().catch(null)

function firstImageUrl(value: string | string[] | null | undefined) {
  if (Array.isArray(value)) return value.find((url) => url.trim().length > 0)?.trim() || null
  if (typeof value === 'string' && value.trim()) return value.split(',')[0]?.trim() || null
  return null
}

/**
 * `fee` is the unit price and `actualQuantity` is what the jastiper really bought,
 * which is why the order total is the sum of `fee * actualQuantity` rather than of
 * the quantities the buyer asked for.
 */
const orderItemPayloadSchema = z
  .object({
    index: z.number().int().nullish(),
    product: z
      .object({
        brand: z.string().nullish(),
        name: z.string().nullish(),
        fee: z.number().nullish(),
        quantity: z.number().nullish(),
        actual_quantity: z.number().nullish(),
        has_bought: z.boolean().nullish(),
        is_unclear: z.boolean().nullish(),
        image_urls: z.union([z.string(), z.array(z.string())]).nullish(),
      })
      .nullish(),
    /** algo-pos-be: `order_items[].chat_history` (not nested under product). */
    chat_history: z.unknown().nullish(),
  })
  .transform((payload) => {
    const rawChat = extractOrderItemChatRaw({
      chat_history: payload.chat_history,
      product: payload.product as Record<string, unknown> | null | undefined,
    })
    const chatHistory = parseChatHistory(rawChat)
    return {
      index: payload.index ?? 0,
      name: payload.product?.name || 'Produk tanpa nama',
      brand: payload.product?.brand || null,
      unitPrice: payload.product?.fee ?? 0,
      quantity: payload.product?.quantity ?? 0,
      actualQuantity: payload.product?.actual_quantity ?? 0,
      hasBought: payload.product?.has_bought ?? false,
      isUnclear: payload.product?.is_unclear ?? false,
      imageUrl: firstImageUrl(payload.product?.image_urls),
      hasChat: orderItemHasChatData(rawChat),
      chatHistory,
    }
  })

export const groupOrderPayloadSchema = z
  .object({
    external_id: z.string(),
    created_at: z.string().nullish(),
    status: groupOrderStatusSchema,
    order_source: z.string().nullish(),
    has_unclear_product: z.boolean().nullish(),
    /** WEBLP-3185 flat list carries the owning group; per-group detail omits it. */
    liotitip_group_id: z.number().int().nullish(),
    group: z
      .object({
        external_id: z.string().nullish(),
        name: z.string().nullish(),
      })
      .nullish(),
    order_items: z.array(orderItemPayloadSchema).nullish(),
    total_order_price: z.number().nullish(),
    total_weight_gram: z.number().nullish(),
    width: z.number().nullish(),
    height: z.number().nullish(),
    length: z.number().nullish(),
    shipment_id: z.string().nullish(),
    stt_no: z.string().nullish(),
    charge_shortlink: z.string().nullish(),
    insurance_billing_rule: orderBillingRuleSchema.nullish().catch(null),
    shipping_billing_rule: orderBillingRuleSchema.nullish().catch(null),
    discount_price: z.number().nonnegative().nullish(),
    billed_counter: z.number().int().nonnegative().nullish(),
    status_message: z.string().nullish(),
    order_receiver: z
      .object({
        name: z.string().nullish(),
        phone_number: z.string().nullish(),
        address: z.string().nullish(),
        additional_address: z.string().nullish(),
        district: z.string().nullish(),
      })
      .nullish(),
    invoice: z
      .object({
        external_id: z.string().nullish(),
        status: invoiceStatusSchema,
        total_amount: z.number().nullish(),
        discount_price: z.number().nullish(),
        discount: z.number().nullish(),
      })
      .nullish(),
  })
  .transform((payload) => ({
    id: payload.external_id,
    createdAt: payload.created_at || null,
    status: payload.status,
    source: payload.order_source || null,
    ...(payload.has_unclear_product != null
      ? { hasUnclearProduct: payload.has_unclear_product }
      : {}),
    // Only present on the flat order list (WEBLP-3185); null under per-group detail.
    groupId: payload.group?.external_id || null,
    groupName: payload.group?.name || null,
    items: payload.order_items ?? [],
    totalPrice: payload.total_order_price ?? 0,
    totalWeightGrams: payload.total_weight_gram ?? 0,
    width: payload.width ?? null,
    height: payload.height ?? null,
    length: payload.length ?? null,
    shipmentId: payload.shipment_id || null,
    sttNo: payload.stt_no || null,
    chargeLink: payload.charge_shortlink || null,
    insuranceBillingRule: payload.insurance_billing_rule,
    shippingBillingRule: payload.shipping_billing_rule,
    discountPrice:
      payload.invoice?.discount_price ?? payload.invoice?.discount ?? payload.discount_price ?? 0,
    billedCount: payload.billed_counter ?? 0,
    statusMessage: payload.status_message || null,
    receiverName: payload.order_receiver?.name || null,
    receiverPhone: payload.order_receiver?.phone_number || null,
    receiverAddress: payload.order_receiver?.address || null,
    receiverAdditionalAddress: payload.order_receiver?.additional_address || null,
    receiverDistrict: payload.order_receiver?.district || null,
    // Invoice identity survives expiry even when the order external ID changes.
    ...(payload.invoice?.external_id ? { invoiceId: payload.invoice.external_id } : {}),
    invoiceStatus: payload.invoice?.status ?? null,
    invoiceTotal: payload.invoice?.total_amount ?? null,
  }))

/** Only the page counters, for the cheap per-tab badge requests. */
export const groupOrderCountPayloadSchema = z
  .object({ pagination: paginationPayloadSchema })
  .transform((payload) => toPagination(payload.pagination, 0).totalCount)

/** Wire format of `GET /v1/algo-pos-be/liotitip/group/{id}/order`. */
export const groupDetailPayloadSchema = z
  .object({
    group: groupPayloadSchema,
    orders: z.array(groupOrderPayloadSchema).nullish(),
    /** Shopping list the agent still has to buy. Shape unconfirmed — only counted. */
    need_to_buy: z.array(z.unknown()).nullish(),
    pagination: paginationPayloadSchema,
  })
  .transform((payload) => ({
    group: payload.group,
    orders: payload.orders ?? [],
    needToBuyCount: payload.need_to_buy?.length ?? 0,
    pagination: toPagination(payload.pagination, payload.orders?.length ?? 0),
  }))

/** Wire format of POST .../order/process. */
export const processGroupOrdersResponseSchema = z
  .object({
    shipment_ids: z.array(z.string()).default([]),
  })
  .transform((payload) => ({ shipmentIds: payload.shipment_ids }))

/** Wire format of GET .../group/{id}/product-name-suggestions. */
export const productNameSuggestionsPayloadSchema = z.object({
  suggestions: z.array(z.string()),
})

export type DashboardPeriod = z.infer<typeof dashboardPeriodSchema>
export type OrderBucket = z.infer<typeof orderBucketSchema>
export type Group = z.infer<typeof groupSchema>
export type GroupList = z.infer<typeof groupListSchema>
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>
export type GroupOrderStatus = z.infer<typeof groupOrderStatusSchema>
export type InvoiceStatus = NonNullable<z.infer<typeof invoiceStatusSchema>>
export type GroupOrder = z.infer<typeof groupDetailPayloadSchema>['orders'][number]
export type GroupOrderItem = GroupOrder['items'][number]
export type GroupDetail = z.infer<typeof groupDetailPayloadSchema>
export type ProcessGroupOrdersResponse = z.infer<typeof processGroupOrdersResponseSchema>
